import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

interface ChatWidgetContextType {
  isOpen: boolean;
  /** Open the widget, optionally seeding the first question to send. */
  openWidget: (question?: string) => void;
  closeWidget: () => void;
  /**
   * Returns the question the widget was opened with (once), or null.
   * Reading it clears it, so a remount can't re-send the same message.
   */
  consumePendingQuestion: () => string | null;
}

const ChatWidgetContext = createContext<ChatWidgetContextType | null>(null);

/** Owns open/closed state for the site-wide floating chat widget. */
export function ChatWidgetProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  // A ref, not state: consuming it must take effect synchronously so a
  // double-invoked effect (StrictMode, or a remount) can't send twice.
  const pendingQuestion = useRef<string | null>(null);

  const openWidget = useCallback((question?: string) => {
    if (question) pendingQuestion.current = question;
    setIsOpen(true);
  }, []);

  const closeWidget = useCallback(() => {
    pendingQuestion.current = null;
    setIsOpen(false);
  }, []);

  const consumePendingQuestion = useCallback(() => {
    const question = pendingQuestion.current;
    pendingQuestion.current = null;
    return question;
  }, []);

  return (
    <ChatWidgetContext.Provider value={{ isOpen, openWidget, closeWidget, consumePendingQuestion }}>
      {children}
    </ChatWidgetContext.Provider>
  );
}

export function useChatWidget() {
  const context = useContext(ChatWidgetContext);
  if (!context) throw new Error('useChatWidget must be used within ChatWidgetProvider');
  return context;
}
